import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AffiliateOrganization, OrganizationUser } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface Props {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
}

const AdminAffiliateOrganization = ({ organizationUser, setAlert }: Props) => {
  const { getToken } = useAuth();
  const [affiliateOrg, setAffiliateOrg] =
    useState<AffiliateOrganization | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAffiliateOrg = async () => {
    if (!organizationUser) return;
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization`,
        "GET",
      );
      setAffiliateOrg(res.affiliateOrg);
    } catch (error) {
      setAlert(
        error.message || "Unable to fetch affiliate organization",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      setActionLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization/approve`,
        "POST",
      );
      setAffiliateOrg(res.affiliateOrg);
      setAlert("Successfully approved affiliate partner", "success");
    } catch (error) {
      setAlert(error.message || "Unable to approve affiliate partner", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const updateAffiliateOrganization = async () => {
    try {
      setActionLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization`,
        "PUT",
        {},
        {
          isActive: affiliateOrg?.isActive,
        },
      );
      setAffiliateOrg(res.affiliateOrg);
      setAlert("Succesfully updated affiliate partner", "success");
    } catch (error) {
      setAlert(error.message || "Unable to update affiliate partner", "error");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Affiliate Partnership Program
      </Typography>

      {loading && <CircularProgress />}

      <Typography paragraph>
        Affiliate Link:{" "}
        <strong>
          {affiliateOrg?.referralLink || "User hasn't joined the program yet"}
        </strong>
      </Typography>

      <Grid container alignItems="center" sx={{ mb: 2 }}>
        Coupon Code:
        <strong>
          {affiliateOrg?.stripePromotionCode || "User has no coupon code."}
        </strong>
        <LoadingButton
          loading={actionLoading}
          onClick={generateCode}
          disableElevation
          variant="contained"
          size="small"
          sx={{ ml: 1 }}
          disabled={Boolean(!affiliateOrg || affiliateOrg?.stripePromotionCode)}
        >
          Create Code
        </LoadingButton>
      </Grid>

      <Grid container gap={1}>
        Status:{" "}
        <Box sx={{ ml: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                onChange={() => {
                  setAffiliateOrg((prev) => {
                    if (!prev) {
                      return prev;
                    }
                    return { ...prev, isActive: !prev.isActive };
                  });
                }}
                checked={affiliateOrg?.isActive}
              />
            }
            label="Affiliate is Active"
          />
        </Box>
        <LoadingButton
          loading={actionLoading}
          onClick={updateAffiliateOrganization}
          disableElevation
          variant="contained"
          size="small"
          disabled={!affiliateOrg}
        >
          Update Status
        </LoadingButton>
      </Grid>
    </>
  );
};

export default AdminAffiliateOrganization;
